<template>
	<span class="inline-block" ref="referenceElement">
		<!-- Trigger -->
		<span
			class="inline-flex items-center tooltip-trigger"
			:tabindex="disabled ? -1 : triggerTabIndex"
			:aria-describedby="isOpen ? contentId : null"
			@mouseenter="onEnter"
			@mouseleave="onLeave"
			@focus="onEnter"
			@blur="onLeave"
			@keydown.esc="hideNow"
		>
			<slot />
		</span>

		<!-- Content -->
		<portal to="tooltip-root" v-if="usePortal">
			<div
				v-show="isOpen"
				class="bg-[#303030] text-white py-2 px-4 rounded-lg text-center text-sm space-y-2"
				ref="floatingElement"
				:id="contentId"
				role="tooltip"
				:style="floatingStyle"
				@mouseenter="onTooltipEnter"
				@mouseleave="onTooltipLeave"
			>
				<div>
					<slot name="content">
						{{ content }}
					</slot>
				</div>
				<div
					v-if="arrow"
					ref="arrowElement"
					class="absolute w-2 h-2 bg-[#303030] transform rotate-45"
					:style="arrowStyle"
				/>
			</div>
		</portal>

		<!-- Non-portal fallback -->
		<div
			v-else
			v-show="isOpen"
			class="bg-[#303030] text-white py-2 px-4 rounded-lg text-center text-sm space-y-2"
			ref="floatingElement"
			:id="contentId"
			role="tooltip"
			:style="floatingStyle"
			@mouseenter="onTooltipEnter"
			@mouseleave="onTooltipLeave"
		>
			<div>
				<slot name="content">
					{{ content }}
				</slot>
			</div>

			<div
				v-if="arrow"
				ref="arrowElement"
				class="absolute w-2 h-2 bg-[#303030] transform rotate-45"
				:style="arrowStyle"
			/>
		</div>
	</span>
</template>

<script>
import {
	computePosition,
	autoUpdate,
	offset,
	flip,
	shift,
	arrow as arrowMw
} from '@floating-ui/dom'

let uid = 0

export default {
	name: 'Tooltip',
	props: {
		reference: {
			type: [Object],
			default: null
		},
		content: {
			type: String,
			default: ''
		},
		placement: {
			type: String,
			default: 'top'
		},
		offset: {
			type: Number,
			default: 8
		},
		open: {
			type: Boolean,
			default: null
		},
		openDelay: {
			type: Number,
			default: 50
		},
		closeDelay: {
			type: Number,
			default: 50
		},
		interactive: {
			type: Boolean,
			default: false
		},
		usePortal: {
			type: Boolean,
			default: true
		},
		disabled: {
			type: Boolean,
			default: false
		},
		maxWidth: {
			type: [Number, String],
			default: 260
		},
		arrow: {
			type: Boolean,
			default: true
		},
		triggerTabIndex: {
			type: [Number, String],
			default: null
		},
		zIndex: {
			type: Number,
			default: 9999
		},
		flip: {
			type: Boolean,
			default: true
		}
	},
	data() {
		uid += 1

		return {
			isOpen: false,
			contentId: `tt-${uid}`,
			cleanupAutoUpdate: null,
			openTimeout: null,
			closeTimeout: null,
			floatingStyle: {
				position: 'fixed',
				left: '0px',
				top: '0px',
				transform: 'translate3d(0,0,0)',
				zIndex: this.zIndex,
				maxWidth:
					typeof this.maxWidth === 'number'
						? `${this.maxWidth}px`
						: this.maxWidth,
				pointerEvents: 'none',
				opacity: 0
			},
			arrowStyle: {}
		}
	},
	computed: {
		isControlled() {
			return this.open !== null
		}
	},
	watch: {
		reference() {
			if (!this.isOpen) {
				return
			}

			// If reference changes, recompute and rebind autoUpdate
			this.stopPositioning()
			this.$nextTick(this.startPositioning)
		},
		open(value) {
			if (!this.isControlled) {
				return
			}

			this.isOpen = !!value
		},
		isOpen(value) {
			if (value) {
				this.$nextTick(this.startPositioning)
			} else {
				this.stopPositioning()
			}
		},
		placement() {
			if (this.isOpen) {
				this.updatePosition()
			}
		},
		offset() {
			if (this.isOpen) {
				this.updatePosition()
			}
		},
		zIndex(value) {
			this.floatingStyle = {
				...this.floatingStyle,
				zIndex: value
			}
		},
		maxWidth(value) {
			this.floatingStyle = {
				...this.floatingStyle,
				maxWidth: typeof value === 'number' ? `${value}px` : value
			}
		},
		disabled(value) {
			if (value) {
				this.hideNow()
			}
		}
	},
	methods: {
		clearTimers() {
			if (this.openTimeout) {
				clearTimeout(this.openTimeout)
			}

			if (this.closeTimeout) {
				clearTimeout(this.closeTimeout)
			}

			this.openTimeout = null
			this.closeTimeout = null
		},
		onEnter() {
			if (this.disabled) {
				return
			}

			this.clearTimers()

			this.openTimeout = setTimeout(() => this.showNow(), this.openDelay)
		},
		onLeave() {
			if (this.disabled) {
				return
			}

			this.clearTimers()

			this.closeTimeout = setTimeout(
				() => this.hideNow(),
				this.closeDelay
			)
		},
		onTooltipEnter() {
			if (!this.interactive) {
				return
			}

			this.clearTimers()
		},
		onTooltipLeave() {
			if (!this.interactive) {
				return
			}

			this.onLeave()
		},
		showNow() {
			if (this.disabled) {
				return
			}

			if (!this.content && !this.$slots.content) {
				return
			}

			if (this.isControlled) {
				return
			}

			this.isOpen = true
		},
		hideNow() {
			if (this.isControlled) {
				return
			}

			this.isOpen = false
		},
		getReferenceElement() {
			if (this.reference) {
				return this.reference
			}

			// tooltip root contains tooltip-trigger, but we want tooltip-trigger for hover/focus area. Reference should be the trigger span for correct geometry.
			return this.$el && this.$el.querySelector('.tooltip-trigger')
		},
		startPositioning() {
			// SSR guard
			if (typeof window === 'undefined') {
				return
			}

			const referenceElement = this.getReferenceElement()
			const floatingElement = this.$refs.floatingElement

			if (!referenceElement || !floatingElement) {
				return
			}

			// interactive tooltips need pointer events enabled
			this.floatingStyle = {
				...this.floatingStyle,
				pointerEvents: this.interactive ? 'auto' : 'none',
				opacity: 1
			}

			this.updatePosition()

			const autoRef =
				referenceElement instanceof Element
					? referenceElement
					: referenceElement.contextElement

			// Auto re-position on scroll/resize/layout shifts
			if (autoRef && autoRef instanceof Element) {
				this.cleanupAutoUpdate = autoUpdate(
					autoRef,
					floatingElement,
					() => {
						this.updatePosition()
					}
				)
			}
		},
		stopPositioning() {
			if (this.cleanupAutoUpdate) {
				this.cleanupAutoUpdate()
				this.cleanupAutoUpdate = null
			}

			this.floatingStyle = {
				...this.floatingStyle,
				opacity: 0,
				pointerEvents: 'none'
			}
		},
		async updatePosition() {
			const referenceElement = this.getReferenceElement()
			const floatingElement = this.$refs.floatingElement

			if (!referenceElement || !floatingElement) {
				return
			}

			const middleware = [offset(this.offset), shift({ padding: 0 })]

			if (this.flip) {
				middleware.push(flip())
			}

			const arrowElement = this.$refs.arrowElement

			if (this.arrow && arrowElement) {
				middleware.push(arrowMw({ element: arrowElement }))
			}

			const { x, y, placement, middlewareData } = await computePosition(
				referenceElement,
				floatingElement,
				{
					placement: this.placement,
					strategy: 'fixed',
					middleware
				}
			)

			this.floatingStyle = {
				...this.floatingStyle,
				top: `${Math.round(y)}px`,
				left: `${Math.round(x)}px`
			}

			if (this.arrow && arrowElement && middlewareData.arrow) {
				const { x: ax, y: ay } = middlewareData.arrow
				const side = placement.split('-')[0]

				// Position arrow on opposite side
				const staticSide = {
					top: 'bottom',
					right: 'left',
					bottom: 'top',
					left: 'right'
				}[side]

				this.arrowStyle = {
					left: ax !== null ? `${Math.round(ax)}px` : '',
					top: ay !== null ? `${Math.round(ay)}px` : '',
					right: '',
					bottom: '',
					[staticSide]: '-4px'
				}
			}
		}
	},
	mounted() {
		if (this.isControlled) {
			this.isOpen = !!this.open
		}
	},
	beforeDestroy() {
		this.clearTimers()
		this.stopPositioning()
	}
}
</script>
